<template>
	<div class="auth-layout">
		<!-- Top Controls -->
		<div class="top-controls-container">
			<v-btn variant="text" icon size="small" @click="toggleDark" :aria-label="isDark ? 'Modo claro' : 'Modo escuro'">
				<v-icon>{{ isDark ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
			</v-btn>
			<LanguageSelector />
		</div>

		<!-- Left Side - Branding -->
		<div class="auth-branding">
			<!-- Random Background Overlay -->
			<div class="background-overlay" :style="overlayStyle"></div>

			<div class="branding-content">
				<div class="logo-container">
					<OriLogo variant="full" :size="280" color="white" />
				</div>
				<div class="branding-text">
					<p class="branding-description">
						{{ $t("auth.systemDescription") }}
					</p>
				</div>
				<div class="branding-features">
					<div class="feature-item">
						<v-icon icon="mdi-account-group" size="20" class="feature-icon" />
						<div class="feature-info">
							<span class="feature-title">{{ $t("auth.features.people") }}</span>
							<span class="feature-desc">{{ $t("auth.features.peopleDesc") }}</span>
						</div>
					</div>
					<div class="feature-item">
						<v-icon icon="mdi-transit-connection-variant" size="20" class="feature-icon" />
						<div class="feature-info">
							<span class="feature-title">{{ $t("auth.features.journey") }}</span>
							<span class="feature-desc">{{ $t("auth.features.journeyDesc") }}</span>
						</div>
					</div>
					<div class="feature-item">
						<v-icon icon="mdi-calendar-star" size="20" class="feature-icon" />
						<div class="feature-info">
							<span class="feature-title">{{ $t("auth.features.rituals") }}</span>
							<span class="feature-desc">{{ $t("auth.features.ritualsDesc") }}</span>
						</div>
					</div>
					<div class="feature-item">
						<v-icon icon="mdi-cash-multiple" size="20" class="feature-icon" />
						<div class="feature-info">
							<span class="feature-title">{{ $t("auth.features.financial") }}</span>
							<span class="feature-desc">{{ $t("auth.features.financialDesc") }}</span>
						</div>
					</div>
					<div class="feature-item">
						<v-icon icon="mdi-television-play" size="20" class="feature-icon" />
						<div class="feature-info">
							<span class="feature-title">{{ $t("auth.features.communication") }}</span>
							<span class="feature-desc">{{ $t("auth.features.communicationDesc") }}</span>
						</div>
					</div>
					<div class="feature-item">
						<v-icon icon="mdi-file-chart" size="20" class="feature-icon" />
						<div class="feature-info">
							<span class="feature-title">{{ $t("auth.features.reports") }}</span>
							<span class="feature-desc">{{ $t("auth.features.reportsDesc") }}</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Right Side - Form -->
		<div class="auth-content">
			<div class="auth-form-container">
				<div v-if="!showMfa" class="auth-header">
					<h1 class="auth-title">{{ $t("auth.welcomeBack") }}</h1>
					<p class="auth-subtitle">{{ $t("auth.loginSubtitle") }}</p>
				</div>

				<v-card class="auth-card" elevation="0" variant="outlined">
					<v-card-text class="pa-8">
						<!-- Etapa 2: verificação MFA -->
						<MfaVerify
							v-if="showMfa"
							@verified="handleMfaVerified"
							@cancel="handleMfaCancel"
						/>

						<form v-else @submit.prevent="handleLogin" class="auth-form">
							<v-text-field
								v-model="email"
								:label="$t('auth.email')"
								type="email"
								variant="outlined"
								prepend-inner-icon="mdi-email"
								required
								class="mb-4"
							/>
							<v-text-field
								v-model="password"
								:label="$t('auth.password')"
								type="password"
								variant="outlined"
								prepend-inner-icon="mdi-lock"
								required
								class="mb-6"
							/>

							<v-btn
								type="submit"
								:loading="loading"
								size="large"
								block
								class="auth-submit-btn mb-4"
							>
								{{ $t("auth.login") }}
							</v-btn>

							<div class="auth-links">
								<router-link to="/forgot-password" class="auth-link forgot-link">
									{{ $t("auth.forgotPassword") }}
								</router-link>
								<router-link to="/" class="auth-link">
									{{ $t("auth.noAccount") }}
								</router-link>
							</div>

							<div class="invite-link-row">
								<router-link to="/convite" class="invite-link">
									<v-icon size="16" class="mr-1">mdi-ticket-confirmation-outline</v-icon>
									{{ $t("auth.haveInviteCode") }}
								</router-link>
							</div>

							<v-alert
								v-if="error"
								type="error"
								variant="tonal"
								class="mt-4"
								closable
								@click:close="error = ''"
							>
								{{ error }}
							</v-alert>
						</form>
					</v-card-text>
				</v-card>
			</div>

			<!-- Legal Footer -->
			<div class="legal-footer">
				<div class="legal-links">
					<router-link to="/termos">{{ $t("landing.termsOfUse") }}</router-link>
					<span>&middot;</span>
					<router-link to="/privacidade">{{ $t("landing.privacyPolicy") }}</router-link>
				</div>
				<p class="legal-contact">ori@oriapp.com.br</p>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useI18n } from "vue-i18n";
import { useRandomBackground } from "@/composables/useRandomBackground";
import { useOrixaTheme } from "@/composables/useOrixaTheme";
import OriLogo from "@/components/common/OriLogo.vue";
import LanguageSelector from "@/components/common/LanguageSelector.vue";
import MfaVerify from "@/components/auth/MfaVerify.vue";

const { t } = useI18n();
const { overlayStyle } = useRandomBackground();
const { toggleDark, isDark } = useOrixaTheme();
const authStore = useAuthStore();

const email = ref("");
const password = ref("");
const error = ref("");
const loading = ref(false);
const showMfa = ref(false);
const router = useRouter();

function proceedAfterLogin() {
	// Verificar se precisa completar onboarding primeiro
	if (authStore.needsOnboarding) {
		router.push({ name: "Onboarding" });
		return;
	}

	// Verificar se há uma URL de redirecionamento na query string
	const redirectUrl = router.currentRoute.value.query.redirect as string;

	if (redirectUrl) {
		router.push(redirectUrl);
	} else {
		router.push("/dashboard");
	}
}

async function handleLogin() {
	error.value = "";
	loading.value = true;

	try {
		const result = await authStore.login(email.value, password.value);

		if (result === "mfa") {
			showMfa.value = true;
		} else if (result === "success") {
			proceedAfterLogin();
		} else {
			error.value = t("auth.messages.loginError");
		}
	} catch (err: any) {
		error.value = err?.response?.data?.message || t("auth.messages.loginError");
		console.error("Erro de login:", err);
	} finally {
		loading.value = false;
	}
}

function handleMfaVerified() {
	showMfa.value = false;
	proceedAfterLogin();
}

function handleMfaCancel() {
	showMfa.value = false;
	authStore.resetMfaChallenge();
	password.value = "";
}
</script>

<style lang="scss" scoped>
@use "@/styles/_variables.scss" as vars;
@use "@/styles/_theme-utilities.scss" as theme;

.auth-layout {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	display: grid;
	grid-template-columns: 1fr 1fr;
	font-family: vars.$font-primary;
	margin: 0;
	padding: 0;
	overflow: hidden;
	z-index: 1000;

	@media (max-width: 968px) {
		grid-template-columns: 1fr;
		.auth-branding {
			display: none;
		}
	}
}

// Language Selector
.top-controls-container {
	position: absolute;
	top: 1rem;
	right: 1rem;
	z-index: 1001;
	display: flex;
	align-items: center;
	gap: 0.25rem;
}

// Left Side - Branding
.auth-branding {
	@include theme.primary-gradient;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 3rem;
	position: relative;
	overflow: hidden;

	// Background overlay com textura padrão
	&::before {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="20" cy="20" r="1" fill="%23ffffff" opacity="0.1"/><circle cx="80" cy="40" r="1" fill="%23ffffff" opacity="0.1"/><circle cx="40" cy="80" r="1" fill="%23ffffff" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
		opacity: 0.3;
		z-index: 0;
	}

	// Background overlay com imagem de orixá
	.background-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		opacity: 0.25;
		z-index: 1;
		filter: blur(1px);
		transition: opacity 0.3s ease;
	}
}

.branding-content {
	text-align: center;
	color: white;
	position: relative;
	z-index: 2;
	max-width: 560px;
}

.logo-container {
	margin-bottom: 3rem;
	display: flex;
	justify-content: center;
	align-items: center;
}

.branding-text {
	margin-bottom: 3rem;

	.branding-title {
		font-size: 2.5rem;
		font-weight: 700;
		margin-bottom: 1rem;
		font-family: vars.$font-heading;
	}

	.branding-description {
		font-size: 1.2rem;
		opacity: 0.9;
		line-height: 1.6;
	}
}

.branding-features {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1.25rem;
}

.feature-item {
	display: flex;
	align-items: flex-start;
	gap: 0.625rem;
	opacity: 0.9;
	text-align: left;

	.feature-icon {
		background: rgba(255, 255, 255, 0.2);
		border-radius: 50%;
		padding: 0.375rem;
		width: 32px;
		height: 32px;
		min-width: 32px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.feature-info {
		display: flex;
		flex-direction: column;
		gap: 0.125rem;
	}

	.feature-title {
		font-size: 0.9rem;
		font-weight: 600;
		line-height: 1.2;
	}

	.feature-desc {
		font-size: 0.75rem;
		opacity: 0.75;
		line-height: 1.3;
	}
}

// Right Side - Form
.auth-content {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 2rem;
	background: rgb(var(--v-theme-surface));
	position: relative;

	&::before {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: linear-gradient(
			135deg,
			rgba(var(--v-theme-primary), 0.02) 0%,
			rgba(var(--v-theme-surface), 1) 50%,
			rgba(var(--v-theme-primary), 0.02) 100%
		);
		z-index: 0;
	}
}

.auth-form-container {
	width: 100%;
	max-width: 440px;
	position: relative;
	z-index: 1;
}

.auth-header {
	text-align: center;
	margin-bottom: 2rem;

	.auth-title {
		font-size: 2.5rem;
		font-weight: 700;
		color: rgb(var(--v-theme-primary));
		margin-bottom: 0.5rem;
		font-family: vars.$font-heading;
	}

	.auth-subtitle {
		color: rgb(var(--v-theme-on-surface));
		font-size: 1.1rem;
		opacity: 0.8;
	}
}

.auth-card {
	@include theme.theme-card;
	background: rgb(var(--v-theme-surface));
	border: 1px solid rgba(var(--v-theme-primary), 0.08);
	border-radius: 24px;
	@include theme.theme-shadow(0.08);

	&:hover {
		transform: none;
		@include theme.theme-shadow(0.12);
	}
}

.auth-form {
	.auth-submit-btn {
		@include theme.theme-button;
		height: 56px;
		font-size: 1.1rem;
		font-weight: 600;
		text-transform: none;
		border-radius: 16px;
		letter-spacing: 0.5px;
	}
}

.auth-links {
	text-align: center;
	display: flex;
	flex-direction: column;
	gap: 0.75rem;

	.auth-link {
		color: rgb(var(--v-theme-primary));
		text-decoration: none;
		font-weight: 500;
		transition: all 0.3s ease;

		&:hover {
			color: rgba(var(--v-theme-primary), 0.8);
			text-decoration: underline;
		}
	}

	.forgot-link {
		font-size: 0.9rem;
		opacity: 0.8;
	}
}

.invite-link-row {
	margin-top: 1.25rem;
	padding-top: 1.25rem;
	border-top: 1px solid rgba(var(--v-theme-on-surface), 0.1);
	text-align: center;

	.invite-link {
		display: inline-flex;
		align-items: center;
		color: rgba(var(--v-theme-on-surface), 0.7);
		text-decoration: none;
		font-size: 0.875rem;
		font-weight: 500;
		transition: color 0.3s ease;

		&:hover {
			color: rgb(var(--v-theme-primary));
		}
	}
}

// Mobile responsiveness
@media (max-width: 768px) {
	.auth-content {
		padding: 1rem;
	}

	.auth-header {
		.auth-title {
			font-size: 2rem;
		}
	}

	.auth-form-container {
		max-width: 100%;
	}
}

@media (max-width: 480px) {
	.auth-header {
		margin-bottom: 1.5rem;

		.auth-title {
			font-size: 1.8rem;
		}

		.auth-subtitle {
			font-size: 1rem;
		}
	}

	.auth-card {
		:deep(.v-card-text) {
			padding: 1.5rem !important;
		}
	}
}

.legal-footer {
	position: absolute;
	bottom: 1rem;
	left: 0;
	right: 0;
	text-align: center;

	.legal-links {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 0.5rem;
		font-size: 0.8rem;
		margin-bottom: 0.25rem;

		a {
			color: rgb(var(--v-theme-on-surface));
			text-decoration: none;
			opacity: 0.5;

			&:hover {
				opacity: 0.8;
			}
		}

		span {
			opacity: 0.3;
		}
	}

	.legal-contact {
		font-size: 0.75rem;
		color: rgb(var(--v-theme-on-surface));
		opacity: 0.4;
		margin: 0;
	}
}
</style>
